/**
 * Screen data for flow B's search frames.
 *
 * Every Bangla string here is copied from a design frame. Nothing is invented —
 * docs/01 § prohibited: "Every Bangla string in the design is final. Missing strings:
 * ask." The frame each string comes from is named above it.
 *
 * In production these come from the Worker (docs/05 § search: responses are already
 * localised, because the client must never compose Bangla from fragments). They are
 * typed as server shapes and held here as the fixture the frames were drawn against, so
 * the visual-regression capture renders the screen that was drawn.
 *
 * Money is integer minor units (paisa) per docs/05 § conventions.
 */
import type { SearchAlternative, SearchDomainNotLive, SearchFilter } from '@oprappo/contracts';

/* --------------------------------------------------------------- B2 · empty --- */

/*
 * ── WHAT WAS HERE, AND WHY IT IS GONE (W2-08) ───────────────────────────────
 * The three সাম্প্রতিক rows B2 draws, and the five মানুষ যা খোঁজেন chips. The screen
 * rendered the three rows as EVERY person's recent searches, one of them a search into a
 * domain that was not live, and the five chips as what people search, whatever people
 * searched.
 *
 * Recents are now the person's own: the Worker's record of a signed-in person's searches
 * (`GET /v1/search/recent`), or this device's record of a signed-out person's
 * (`./recent-store.ts`). Popular chips are the Worker's (`GET /v1/search/popular`):
 * this week's demand, with the five B2 draws as ITS fallback, said to be drawn. Neither is
 * kept here, for the reason the results constants below were removed: a constant the
 * screen falls back to is the defect, restored.
 */

/** B2 headings and the pinned AI card, exactly as drawn. */
export const B2_COPY = {
  placeholder_bn: 'কী প্রয়োজন?',
  recents_heading_bn: 'সাম্প্রতিক',
  popular_heading_bn: 'মানুষ যা খোঁজেন',
  ai_card_title_bn: 'পুরো কথাটা লিখে ফেলুন',
  ai_card_body_bn: 'এক শব্দের বেশি লিখলে অপ্রাপ্য এআই ধরে নেবে।',
} as const;

/* ------------------------------------------------------------- B3 · results --- */

/**
 * B3 · সার্চ ফলাফল — the four filter chips. `সব` is drawn selected.
 *
 * The ids are the Worker's filter keys (`@oprappo/contracts § SEARCH_FILTERS`): a chip is
 * a question the Worker answers now, not a highlight.
 */
export const RESULT_FILTERS: readonly { id: SearchFilter; label_bn: string }[] = [
  { id: 'all', label_bn: 'সব' },
  { id: 'nearby', label_bn: 'কাছাকাছি' },
  { id: 'verified', label_bn: 'যাচাইকৃত' },
  { id: 'today', label_bn: 'আজ' },
];

/**
 * The drawn query B3's frame is captured against.
 *
 * Kept because it names the FIXTURE, not because it is content: the visual gate loads
 * `/search?q=এসি মেরামত` and `fixtures/api/search.routes.json` answers that exact body.
 * `__tests__/fixtures.test.ts` asserts the two stay in step.
 */
export const B3_QUERY_BN = 'এসি মেরামত';

/*
 * B3's footer strip is gone (W2-08). It told every searcher the catalogue holds free
 * resources and courses further down — "scroll down" — on every answer, whether or not
 * anything was below and whether or not the catalogue held either.
 * `docs/deviations/W2-08.md` records the row.
 */

/**
 * ── WHAT WAS HERE, AND WHY IT IS GONE ───────────────────────────────────────
 * `B3_PROFESSIONALS`, `B3_PACKAGES` and the four heading/count constants
 * (`টেকনিশিয়ান`, `১২ জন`, `সার্ভিস প্যাকেজ`, `৩টি`). The screen rendered them for EVERY
 * query, so a search for a plumber answered with an air-conditioning technician, a rating
 * of ৪.৭ ★ nobody had earned, ২৩০ কাজ nobody had done and an ETA nobody had promised.
 *
 * The results list renders `result.groups` from `POST /v1/search`
 * (`./client.ts § SearchResultGroup`). The two drawn headings and their two count phrases
 * belong to the SERVER and are there now — `apps/api/src/search/copy.ts § DRAWN_GROUPS`,
 * transcribed from the same frame this file transcribes and held against it by that
 * package's own copy test. What did NOT move with them is the nine offering types nobody
 * drew a heading for: those still arrive empty, because `professional` covers a lawyer as
 * well as a technician and generalising two headings to eleven types is extrapolation.
 * `docs/bangla-needed/search-groups.md` lists them.
 *
 * They are not restored here under any name. A constant in the bundle that the screen
 * falls back to when the server is thin is the exact defect this change removed, and it
 * would be invisible again within a week.
 */

/* -------------------------------------------- state 11 · সেবাটি আসছে (B3) --- */

/**
 * `Oprappo P0 States.dc.html`, "১১ · সেবাটি আসছে" — a search into a domain that is not
 * live. Frame note: "the search logged as demand — so the state feeds the roadmap
 * instead of just apologising."
 *
 * The drawn instance is the health domain. Every string is server-supplied in
 * production (docs/05 § errors: `E_DOMAIN_NOT_LIVE` carries `alternatives[]` and
 * `notify_available: true`); this is the fixture the frame was drawn against.
 */
export const DOMAIN_NOT_LIVE_QUERY_BN = 'ডাক্তার দেখাতে চাই';

/**
 * The frame's words only. Each row's `to` is the Worker's — the listing it found that makes
 * the row true — and is not drawn, so it is not here.
 */
export type DrawnDomainNotLive = Omit<SearchDomainNotLive, 'alternatives'> & {
  alternatives: Omit<SearchAlternative, 'to'>[];
};

export const DOMAIN_NOT_LIVE: DrawnDomainNotLive = {
  title_bn: 'স্বাস্থ্য সেবা এখনও চালু হয়নি',
  body_bn: 'ডাক্তার ও ডায়াগনস্টিক অপ্রাপ্য-তে আসছে। ভুল জায়গায় পাঠিয়ে আপনার সময় নষ্ট করতে চাই না।',
  notify_available: true,
  alternatives: [
    { id: 'medicine', label_bn: 'ঔষধ কিনুন', where_bn: 'কেনাকাটায় চালু আছে' },
    { id: 'ambulance', label_bn: 'অ্যাম্বুলেন্সের নম্বর', where_bn: 'ফ্রি রিসোর্স · কল করা যাবে' },
  ],
  demand_note_bn:
    'আপনার এই খোঁজটি চাহিদার তালিকায় যোগ হলো। যে ক্ষেত্রে সবচেয়ে বেশি খোঁজা হয়, সেটিই আগে চালু হয়।',
  coverage_bn: 'পাঁচটি ক্ষেত্র এখন চালু · সাতটি আসছে',
};

/** Fixed labels drawn in the state-11 frame. */
export const DOMAIN_NOT_LIVE_COPY = {
  notify_action_bn: 'চালু হলে জানান',
  alternatives_heading_bn: 'এখন যা করতে পারেন',
} as const;

/* ------------------------------------------ what replaced the canned server --- */

/**
 * The AI fixture: the query C1/C2/B4 are all drawn against. The Worker answers this one
 * with `route: "ai"` and a conversation id, and the answer is rendered in the thread —
 * C2 is where the understood intent and its `না, অন্য কিছু` control are drawn.
 */
export const AI_QUERY_BN = 'আমার এসি নষ্ট, আজই কাউকে দরকার';

/**
 * ── THE CANNED SERVER IS GONE, AND THAT IS THE POINT ────────────────────────
 * `cannedSearchResponse(q)` used to stand in for `postSearch` while no route existed.
 * `POST /v1/search` exists now, `useSearch` calls it, and the screen renders what comes
 * back — so a second source of answers inside the bundle would be a second code path,
 * scored by the visual gate and never seen by a user (`fixtures/api/README.md` names
 * this as the fix NOT to take).
 *
 * The drawn answers moved to `fixtures/api/search.*.json`, where the gate serves them at
 * the network boundary: same bundle, same fetch, same rendering, answered by drawn
 * values instead of a database. `__tests__/fixtures.test.ts` asserts those files still
 * carry the strings this file transcribes, and the API's own suite asserts they are
 * exactly what the route returns.
 *
 * `DOMAIN_NOT_LIVE` below stays because it is the DRAWN frame, and it is now the thing
 * the fixture is checked against rather than the thing the screen renders.
 * ─────────────────────────────────────────────────────────────────────────────
 */
export const SEARCH_FIXTURE_DIR = 'fixtures/api';

/* ------------------------------------------------------------- B4 · voice --- */

/**
 * B4 · ভয়েস — the two FIXED labels, exactly as drawn.
 *
 * `transcript_line1_bn` and `transcript_line2_bn` used to live here, holding
 * `আমার এসি নষ্ট, আজই কাউকে` / `দরকার`. The screen rendered them under the word
 * `শুনছি…` with the microphone switched off, so it heard the same sentence from every
 * person who opened it and `শেষ` searched for somebody else's broken air conditioner.
 * The transcript now comes from the Web Speech API (`./voice.tsx`), and where there is no
 * microphone there is no transcript and no `শুনছি…` either — the word means "I am
 * listening", and a screen that is not listening must not say it.
 *
 * The drawn sentence itself is not lost: it is `AI_QUERY_BN` above, which is the query
 * C1, C2 and B4 are all drawn against.
 */
export const B4_COPY = {
  listening_bn: 'শুনছি…',
  done_bn: 'শেষ',
} as const;

/**
 * The seven bars, in the drawn order. The amber one is the peak.
 *
 * The heights are carried as Tailwind classes rather than as CSS values because a CSS
 * value would have to be applied through a `style` attribute, and the CSP in
 * `apps/web/public/_headers` governs those under `style-src`. Each percentage is the
 * drawn one, unchanged: `38%` is `h-[38%]`.
 */
export const B4_WAVEFORM: readonly { heightClass: string; amber: boolean }[] = [
  { heightClass: 'h-[38%]', amber: false },
  { heightClass: 'h-[72%]', amber: false },
  { heightClass: 'h-[50%]', amber: false },
  { heightClass: 'h-[96%]', amber: true },
  { heightClass: 'h-[64%]', amber: false },
  { heightClass: 'h-[44%]', amber: false },
  { heightClass: 'h-[80%]', amber: false },
];
